// 📊 Mesure d'audience avec Umami, installé sur le serveur d'Acadeclic (stats.acadeclic.fr). Sans
// cookie, rien n'est gardé dans le navigateur (sauf le refus de la personne, à sa demande).
// Éteinte par défaut : le script n'est chargé que si REACT_APP_UMAMI_URL et
// REACT_APP_UMAMI_WEBSITE_ID sont fixées au build, et il ne compte que sur acadeclic.fr.
// Aucun jeton dans les adresses envoyées : /forgot-password/:id et /unSubscribe/:id/:lang
// deviennent /forgot-password et /unSubscribe, seuls les paramètres de campagne (utm_…) restent.
// Installation du serveur : deploy/umami/README.md (dépôt de l'API).

export const DOMAINS = "acadeclic.fr,www.acadeclic.fr";
// Fonction appelée par le script d'Umami avant chaque envoi (attribut data-before-send)
export const BEFORE_SEND = "acadeclicStatsBeforeSend";
// Clé lue par le script d'Umami : mesure refusée sur ce navigateur
export const OPT_OUT_KEY = "umami.disabled";
export const EVENTS = { signup: "inscription", paidBooking: "réservation payée" };

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const MASKED_PATHS = [
  [/^\/forgot-password(\/.*)?$/i, "/forgot-password"],
  [/^\/unSubscribe(\/.*)?$/i, "/unSubscribe"],
  // 🔁 « Ne plus recevoir ces rappels » des séries : identifiant du compte dans l'adresse
  [/^\/seriesReminders(\/.*)?$/i, "/seriesReminders"],
  // 📒 Liens de l'e-mail du parent (carnet de suivi) : identifiant de l'élève dans l'adresse
  [/^\/carnetParent(\/.*)?$/i, "/carnetParent"],
  // 🔗 Fiches d'un prof à l'adresse lisible (tour 60) : comptées ensemble, comme /Teacher-Details?advert=…
  [/^\/prof(\/.*)?$/i, "/prof"],
];
const CAMPAIGN_PARAMS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];
const ACCOUNTS = { 1: "prof", 2: "élève" };
const SIGNUP_WAYS = { 0: "e-mail", 1: "google", 2: "facebook" };

// { url, websiteId } si les deux variables sont valides, sinon null (mesure éteinte)
export const analyticsConfig = (env = process.env) => {
  const url = String(env.REACT_APP_UMAMI_URL || "").trim().replace(/\/+$/, "");
  const websiteId = String(env.REACT_APP_UMAMI_WEBSITE_ID || "").trim();
  return /^https:\/\/[^/\s]+$/.test(url) && UUID.test(websiteId) ? { url, websiteId } : null;
};

const maskPath = (pathname) => (MASKED_PATHS.find(([pattern]) => pattern.test(pathname)) || [null, pathname])[1];

// Adresse complète (page vue) ou chemin seul (référent du site), sous la même forme qu'à l'arrivée.
// Autre site : son domaine seulement.
export const maskUrl = (raw, origin = window.location.origin) => {
  if (!raw) return raw;
  let url;
  try {
    url = new URL(raw, origin);
  } catch {
    return "";
  }
  if (!/^https?:$/.test(url.protocol)) return "";
  if (url.origin !== origin) return url.origin;
  const kept = new URLSearchParams();
  CAMPAIGN_PARAMS.forEach((key) => url.searchParams.has(key) && kept.set(key, url.searchParams.get(key)));
  const query = kept.toString();
  const masked = `${maskPath(url.pathname)}${query ? `?${query}` : ""}`;
  return raw.startsWith("/") ? masked : `${url.origin}${masked}`;
};

export const beforeSend = (type, payload) =>
  payload && { ...payload, url: maskUrl(payload.url), referrer: maskUrl(payload.referrer) };

// Chargement du script d'Umami (une fois). Renvoie true s'il vient d'être ajouté.
export const loadAnalytics = (env = process.env, doc = document) => {
  const config = analyticsConfig(env);
  if (!config || doc.querySelector("script[data-website-id]")) return false;
  window[BEFORE_SEND] = beforeSend;
  const script = doc.createElement("script");
  script.defer = true;
  script.src = `${config.url}/script.js`;
  Object.entries({
    "data-website-id": config.websiteId,
    "data-domains": DOMAINS,
    "data-before-send": BEFORE_SEND,
    "data-exclude-hash": "true",
    "data-do-not-track": "true",
  }).forEach(([name, value]) => script.setAttribute(name, value));
  doc.head.appendChild(script);
  return true;
};

// Événement envoyé si le script est là (mesure allumée, pas de bloqueur), sans jamais gêner le site
const trackEvent = (name, data) => {
  try {
    window.umami?.track(name, data)?.catch?.(() => {});
  } catch {
    // mesure d'audience jamais bloquante
  }
};

// 🎯 Inscription terminée : type de compte (userType 1 prof, 2 élève) et moyen (Ptype 0 e-mail,
// 1 Google, 2 Facebook). Jamais de nom, d'e-mail ni d'identifiant.
export const trackSignup = (userType, ptype) =>
  trackEvent(EVENTS.signup, { compte: ACCOUNTS[userType] || "inconnu", via: SIGNUP_WAYS[ptype] || "inconnu" });

// 🎯 Réservation payée : demande instantanée (« class »), cours réservé (« booking ») ou 🔁 série de
// cours (« series »), par carte ou entièrement avec le porte-monnaie. Jamais de montant ni
// d'identifiant. Une fois par paiement (key : session Stripe), même si la page de retour s'affiche
// deux fois.
const COURSE_KINDS = { booking: "réservé", series: "série" };
const trackedPayments = new Set();
export const trackPaidBooking = (kind, payment, key) => {
  if (key && trackedPayments.has(key)) return;
  if (key) trackedPayments.add(key);
  trackEvent(EVENTS.paidBooking, { cours: COURSE_KINDS[kind] || "instantané", paiement: payment === "card" ? "carte" : "porte-monnaie" });
};

// Refus de la mesure sur ce navigateur (politique de confidentialité)
export const optedOut = () => {
  try {
    return !!window.localStorage.getItem(OPT_OUT_KEY);
  } catch {
    return false;
  }
};

export const setOptOut = (out) => {
  try {
    if (out) window.localStorage.setItem(OPT_OUT_KEY, "1");
    else window.localStorage.removeItem(OPT_OUT_KEY);
  } catch {
    // stockage indisponible (navigation privée stricte) : rien à garder
  }
};
